import type { NestResult2D, UsedSheet2D, PlacedPart2D } from '@app/linecut-core';
import { Collapsible } from '../../components/Collapsible';

export interface SheetGroupResult {
  groupLabel: string;
  groupKey: string;
  result: NestResult2D;
}

interface Props {
  groups: SheetGroupResult[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

// TODO: раскомментировать цвета деталей когда понадобится визуализация раскроя листа
// const PIECE_COLORS = [
//   '#e05c5c', '#5b8dd9', '#5cae6e', '#d48f3e',
//   '#8b6fd4', '#3eb8d4', '#d47a3e', '#7ab83e',
//   '#d43e8b', '#3ed4b8',
// ];
const BORDER = '1px solid var(--color-track)';

function fmtArea(mm2: number): string {
  return `${(mm2 / 1_000_000).toFixed(3)} м²`;
}

// TODO: раскомментировать buildColorMap и SheetDiagram когда понадобится SVG-визуализация раскроя листа
// function buildColorMap(sheets: UsedSheet2D[]): Map<string, string> { ... }
// function SheetDiagram({ sheet, colorMap, kerf }) { ... }

// ─── Sheet card ───────────────────────────────────────────────────────────────

function SheetCard({ sheet, count }: { sheet: UsedSheet2D; count: number }) {
  const effColor =
    sheet.efficiency >= 90 ? 'var(--color-in-stock)' :
    sheet.efficiency >= 70 ? '#d97706' :
    'var(--color-red)';

  return (
    <div style={{ background: 'var(--color-white)', border: BORDER, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {count > 1 && (
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-gray)' }}>{count}×</span>
        )}
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)' }}>
          {Math.round(sheet.w)}×{Math.round(sheet.h)} мм
        </span>
        <span style={{ fontSize: 12, color: 'var(--color-white)', background: 'var(--color-gray)', borderRadius: 4, padding: '1px 6px' }}>
          {sheet.label}
        </span>
        <span style={{ fontSize: 12, color: 'var(--color-gray)', marginLeft: 'auto' }}>
          {sheet.placements.length} дет. ·{' '}
          <span style={{ fontWeight: 600, color: effColor }}>{sheet.efficiency}% эффективность</span>
          {' '}· отход {fmtArea(sheet.totalArea - sheet.usedArea)}
        </span>
      </div>

      {/* Part list */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {groupPlacements(sheet.placements).map(({ w, h, name, count: cnt }, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: 'var(--color-bg)', border: BORDER, borderRadius: 4, fontSize: 12, color: 'var(--color-black)' }}>
            {cnt > 1 && <span style={{ fontWeight: 600 }}>{cnt}×</span>}
            {name !== `${Math.round(w)}×${Math.round(h)}` ? `${name} ` : ''}{Math.round(w)}×{Math.round(h)} мм
          </span>
        ))}
      </div>
    </div>
  );
}

function groupPlacements(placements: PlacedPart2D[]): Array<{ w: number; h: number; name: string; count: number }> {
  const map = new Map<string, { w: number; h: number; name: string; count: number }>();
  for (const p of placements) {
    const key = `${p.w}x${p.h}:${p.name}`;
    const entry = map.get(key);
    if (entry) entry.count++;
    else map.set(key, { w: p.w, h: p.h, name: p.name, count: 1 });
  }
  return Array.from(map.values());
}

// ─── Group deduplication helper ───────────────────────────────────────────────

function deduplicateSheets(sheets: UsedSheet2D[]): Array<{ sheet: UsedSheet2D; count: number }> {
  const groups = new Map<string, { sheet: UsedSheet2D; count: number }>();
  for (const sheet of sheets) {
    const key = `${sheet.w}x${sheet.h}:${sheet.placements.map((p) => `${p.partId}@${p.x},${p.y}`).join('|')}`;
    const entry = groups.get(key);
    if (entry) entry.count++;
    else groups.set(key, { sheet, count: 1 });
  }
  return Array.from(groups.values());
}

// ─── Group result card ────────────────────────────────────────────────────────

function GroupResultCard({ groupLabel, result }: { groupLabel: string; result: NestResult2D }) {
  const { totalSheets, totalParts, efficiency, unplacedCount, unplacedParts, usedSheets, totalWasteArea } = result;
  const effColor = efficiency >= 90 ? 'var(--color-in-stock)' : efficiency >= 70 ? '#d97706' : 'var(--color-red)';
  const sheetGroups = deduplicateSheets(usedSheets);

  const badge = unplacedCount > 0 ? (
    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-red)', background: 'var(--color-red-subtle)', padding: '2px 8px', borderRadius: 4 }}>
      ⚠ {unplacedCount} не влезло
    </span>
  ) : null;

  return (
    <Collapsible title={<span style={{ fontFamily: 'Montserrat, sans-serif' }}>{groupLabel}</span>} defaultOpen={false} headerRight={badge ?? undefined}>
      <div className="space-y-3 p-4">
        {/* Stats */}
        <div style={{ display: 'flex', background: 'var(--color-white)', border: BORDER, borderRadius: 8, overflow: 'hidden' }}>
          {[
            { value: totalSheets,             label: 'заготовок',         color: 'var(--color-black)' },
            { value: totalParts,              label: 'деталей размещено', color: 'var(--color-black)' },
            { value: `${efficiency}%`,        label: 'эффективность',     color: effColor },
            { value: fmtArea(totalWasteArea), label: 'общий отход',       color: 'var(--color-black)' },
          ].map(({ value, label, color }, i, arr) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 8px', gap: 4, borderRight: i < arr.length - 1 ? BORDER : 'none' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1, textAlign: 'center' }}>{value}</span>
              <span style={{ fontSize: 12, color: 'var(--color-gray)', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Unplaced */}
        {unplacedCount > 0 && (
          <div style={{ background: 'var(--color-red-subtle)', border: '1px solid rgba(206,43,39,0.2)', borderRadius: 8, padding: 16 }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--color-red)' }}>
              Не удалось разместить ({unplacedCount}):
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unplacedParts.map((p, i) => (
                <span key={i} style={{ padding: '4px 10px', background: 'var(--color-white)', border: '1px solid rgba(206,43,39,0.3)', borderRadius: 4, fontSize: 12, color: 'var(--color-red)' }}>
                  {p.name} · {Math.round(p.w)}×{Math.round(p.h)} мм
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sheet diagrams */}
        <div className="space-y-2">
          {sheetGroups.map(({ sheet, count }, i) => (
            <SheetCard key={i} sheet={sheet} count={count} />
          ))}
        </div>
      </div>
    </Collapsible>
  );
}

// ─── Summary ──────────────────────────────────────────────────────────────────

function SummaryHeader({ groups }: { groups: SheetGroupResult[] }) {
  const totalSheets = groups.reduce((s, g) => s + g.result.totalSheets, 0);
  const totalParts  = groups.reduce((s, g) => s + g.result.totalParts,  0);
  const totalArea   = groups.reduce((s, g) => s + g.result.usedSheets.reduce((a, sh) => a + sh.totalArea, 0), 0);
  const usedArea    = groups.reduce((s, g) => s + g.result.usedSheets.reduce((a, sh) => a + sh.usedArea,  0), 0);
  const eff = totalArea > 0 ? Math.round((usedArea / totalArea) * 100) : 0;
  const effColor = eff >= 90 ? 'var(--color-in-stock)' : eff >= 70 ? '#d97706' : 'var(--color-red)';

  return (
    <div style={{ background: 'var(--color-white)', border: BORDER, borderRadius: 8, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--color-black)', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 8 }}>
        Листовой раскрой
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-black)', fontFamily: 'Montserrat, sans-serif' }}>{totalSheets}</span>
        <span style={{ fontSize: 12, color: 'var(--color-gray)' }}>заготовок</span>
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-black)', fontFamily: 'Montserrat, sans-serif' }}>{totalParts}</span>
        <span style={{ fontSize: 12, color: 'var(--color-gray)' }}>деталей размещено</span>
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: effColor, fontFamily: 'Montserrat, sans-serif' }}>{eff}%</span>
        <span style={{ fontSize: 12, color: 'var(--color-gray)' }}>эффективность</span>
      </span>
    </div>
  );
}

// ─── SheetCalcResultView ──────────────────────────────────────────────────────

export function SheetCalcResultView({ groups }: Props) {
  if (groups.length === 0) return null;

  return (
    <div className="space-y-4">
      <SummaryHeader groups={groups} />
      {groups.map(({ groupLabel, groupKey, result }) => (
        <GroupResultCard key={groupKey} groupLabel={groupLabel} result={result} />
      ))}
    </div>
  );
}
